import { useState } from 'react';
import { AirVent, Wifi, DoorClosed, Usb, Armchair, MapPin, Clock, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { Rating } from './Rating';
import { AvailabilityPill } from './AvailabilityPill';
import { Button } from './Button';
import { SeatMap } from './SeatMap';
import { SeatSelectionModal } from './SeatSelectionModal';

interface DepartureCardProps {
  company: string;
  rating: number;
  ratingCount: number;
  price: number;
  departureTime: string;
  departureLocation: string;
  arrivalTime: string;
  arrivalLocation: string;
  duration: string;
  amenities: string[];
  availableSeats: number;
  discount?: number;
  vehicleType?: string;
  onSelectSeats?: (data: any, seats: string[]) => void;
  onExpand?: (index: number, isExpanded: boolean) => void;
  isExpanded?: boolean;
}

const amenityIcons: { [key: string]: React.ComponentType<{ className?: string }> } = {
  'A/C': AirVent,
  'Wi-Fi': Wifi,
  'Baño': DoorClosed,
  'USB': Usb,
  'Asientos reclinables': Armchair,
};

export const DepartureCard = ({
  company,
  rating,
  ratingCount,
  price,
  departureTime,
  departureLocation,
  arrivalTime,
  arrivalLocation,
  duration,
  amenities,
  availableSeats,
  discount,
  vehicleType,
  onSelectSeats,
  onExpand,
  isExpanded,
}: DepartureCardProps) => {
  // 🛡️ Defensive Guards
  const safeAmenities = amenities ?? [];
  const safePrice = price ?? 0;
  const safeDiscount = discount ?? 0;
  const safeAvailableSeats = availableSeats ?? 0;
  const safeRating = rating ?? 0;
  const safeRatingCount = ratingCount ?? 0;
  const safeCompany = company ?? 'Sin nombre';
  const safeDepartureTime = departureTime ?? '--:--';
  const safeDepartureLocation = departureLocation ?? 'Sin ubicación';
  const safeArrivalTime = arrivalTime ?? '--:--';
  const safeArrivalLocation = arrivalLocation ?? 'Sin ubicación';
  const safeDuration = duration ?? '0h';
  const safeVehicleType = vehicleType ?? 'Bus';
  
  const [activeTab, setActiveTab] = useState<'seats' | 'amenities' | 'reviews'>('seats');
  const [selectedSeats, setSelectedSeats] = useState<string[]>([]);
  const [showMobileModal, setShowMobileModal] = useState(false);

  const handleSeatSelect = (seatNumber: string) => {
    setSelectedSeats(prev => 
      prev.includes(seatNumber) 
        ? prev.filter(s => s !== seatNumber)
        : [...prev, seatNumber]
    );
  };

  const handleSelectSeatsClick = () => {
    // Mobile: Open modal
    if (window.innerWidth < 1024) {
      setShowMobileModal(true);
    } else {
      // Desktop: Expand card
      if (onExpand) {
        // We don't need to pass index since parent manages it
        onExpand(0, !isExpanded);
      }
      if (!isExpanded) {
        setActiveTab('seats');
      }
    }
  };

  const handleContinue = () => {
    if (onSelectSeats) {
      onSelectSeats({
        company: safeCompany,
        rating: safeRating,
        ratingCount: safeRatingCount,
        price: safePrice,
        departureTime: safeDepartureTime,
        departureLocation: safeDepartureLocation,
        arrivalTime: safeArrivalTime,
        arrivalLocation: safeArrivalLocation,
        duration: safeDuration,
        amenities: safeAmenities,
        availableSeats: safeAvailableSeats,
        discount: safeDiscount,
        vehicleType: safeVehicleType,
      }, selectedSeats);
    }
  };

  const safeSelectedSeats = selectedSeats ?? [];
  const totalPrice = safePrice * safeSelectedSeats.length;

  return (
    <>
      {/* Mobile Layout */}
      <div className="lg:hidden bg-white rounded-[16px] border border-[#E5E7EB] shadow-sm p-4">
        {/* Discount Badge */}
        {discount && (
          <div className="flex items-center gap-2 mb-3">
            <div className="px-2.5 py-1 bg-[#FFF5F0] border border-[#FF6A2A] rounded-[8px]">
              <span className="text-xs font-bold text-[#FF6A2A]">
                {discount}% off en Mexmo
              </span>
            </div>
          </div>
        )}

        {/* Company & Rating */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-base font-bold text-[#111827] mb-1">{company}</h3>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Star className="w-3.5 h-3.5 fill-[#FF6A2A] text-[#FF6A2A]" />
                <span className="text-sm font-semibold text-[#111827]">{rating}</span>
              </div>
              {vehicleType && (
                <span className="px-2 py-0.5 bg-[#F7F8FA] text-[#6B7280] text-xs font-medium rounded-[6px]">
                  {vehicleType}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-[#111827]">
              ${price.toLocaleString('es-MX')}
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="flex items-center gap-3 mb-3">
          <div className="text-center">
            <div className="text-lg font-bold text-[#111827]">{departureTime}</div>
          </div>
          <div className="flex-1 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#FF6A2A] flex-shrink-0"></div>
            <div className="flex-1 border-t-2 border-dotted border-[#E5E7EB] relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-2 py-0.5 bg-[#FF6A2A] text-white text-xs font-semibold rounded-full whitespace-nowrap">
                {duration}
              </div>
            </div>
            <div className="w-2 h-2 rounded-full bg-[#FF6A2A] flex-shrink-0"></div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-[#111827]">{arrivalTime}</div>
          </div>
        </div>

        {/* Amenities */}
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#E5E7EB]">
          <Wifi className="w-4 h-4 text-[#6B7280]" />
          <span className="text-xs text-[#6B7280]">+{amenities.length} Amenidades</span>
        </div>

        {/* Bottom: CTA */}
        <div className="flex items-center justify-end">
          <button
            onClick={handleSelectSeatsClick}
            className="px-4 py-2 bg-[#10B981] hover:bg-[#059669] text-white text-sm font-semibold rounded-[10px] transition-colors"
          >
            {availableSeats} Asientos
          </button>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:block bg-white rounded-[16px] border border-[#E5E7EB] shadow-sm hover:shadow-lg hover:border-[#FF6A2A] transition-all group">
        <div className="grid grid-cols-12 gap-6 p-6">
          {/* Left Section: Departure Details */}
          <div className="col-span-12 md:col-span-3 border-r border-[#E5E7EB] pr-6">
            <div className="text-3xl font-bold text-[#111827] mb-1">{departureTime}</div>
            <div className="text-sm font-medium text-[#111827] mb-3">{departureLocation}</div>
            <div className="flex items-center gap-2 text-xs text-[#6B7280] mb-2">
              <Clock className="w-4 h-4 text-[#FF6A2A]" />
              <span>{duration}</span>
            </div>
            <p className="text-xs text-[#6B7280] leading-relaxed">
              Salida desde la terminal principal. Favor de llegar 15 minutos antes.
            </p>
          </div>

          {/* Center Section: Arrival Details */}
          <div className="col-span-12 md:col-span-3 border-r border-[#E5E7EB] pr-6">
            <div className="text-3xl font-bold text-[#111827] mb-1">{arrivalTime}</div>
            <div className="text-sm font-medium text-[#111827] mb-3">{arrivalLocation}</div>
            <div className="relative mb-3">
              <div className="flex items-center">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF6A2A]"></div>
                <div className="flex-1 border-t-2 border-dotted border-[#E5E7EB] mx-2"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF6A2A]"></div>
              </div>
            </div>
            <p className="text-xs text-[#6B7280] leading-relaxed">
              Llegada a la terminal central. Equipaje incluido en el precio del boleto.
            </p>
          </div>

          {/* Right Section: Company, Price & CTA */}
          <div className="col-span-12 md:col-span-6 flex flex-col justify-between">
            {/* Top: Company Info */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-bold text-[#111827] mb-1">{company}</h3>
                  <div className="flex items-center gap-2">
                    <Rating rating={rating} count={ratingCount} size="sm" />
                    {vehicleType && (
                      <span className="px-2 py-1 bg-[#F7F8FA] text-[#6B7280] text-xs font-medium rounded-[8px]">
                        {vehicleType}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Amenities */}
              <div className="flex flex-wrap gap-2 mb-3">
                {amenities.slice(0, 5).map((amenity, index) => {
                  const Icon = amenityIcons[amenity] || AirVent;
                  return (
                    <div 
                      key={index} 
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#F7F8FA] rounded-[8px]"
                      title={amenity}
                    >
                      <Icon className="w-3.5 h-3.5 text-[#6B7280]" />
                      <span className="text-xs text-[#6B7280]">{amenity}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bottom: Price & CTA */}
            <div className="flex items-end justify-between">
              {/* Discount Badge */}
              <div>
                {discount && (
                  <div className="flex items-center gap-2 mb-3">
                    <div className="px-2.5 py-1 bg-[#FFF5F0] border border-[#FF6A2A] rounded-[8px] flex items-center gap-1.5">
                      <span className="text-xs font-bold text-[#FF6A2A]">
                        {discount}% off en Mexmo
                      </span>
                    </div>
                  </div>
                )}
                <div className="mb-1">
                  <span className="text-xs text-[#6B7280] line-through mr-2">
                    {discount ? `$ ${Math.round(price * (1 + discount / 100))}` : ''}
                  </span>
                  <span className="text-xs text-[#6B7280]">Precio</span>
                </div>
                <div className="text-3xl font-bold text-[#111827]">
                  ${price.toLocaleString('es-MX')}
                  <span className="text-sm font-normal text-[#6B7280] ml-1">incl. impuestos</span>
                </div>
              </div>

              {/* CTA Section */}
              <div className="flex flex-col items-end gap-2">
                <AvailabilityPill seats={availableSeats} />
                <Button 
                  variant="primary" 
                  className="px-8 py-3 text-base flex items-center gap-2" 
                  onClick={handleSelectSeatsClick}
                >
                  Seleccionar asientos
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Amenities Count Badge */}
            {amenities.length > 5 && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-[#FF6A2A]">
                <Wifi className="w-3.5 h-3.5" />
                <span>+{amenities.length - 5} servicios</span>
              </div>
            )}
          </div>
        </div>

        {/* Expanded Content - Desktop Only */}
        {isExpanded && (
          <div className="border-t border-[#E5E7EB]">
            {/* Tabs */}
            <div className="flex gap-2 px-6 pt-4">
              <button
                onClick={() => setActiveTab('seats')}
                className={`px-4 py-2 rounded-t-[10px] font-semibold text-sm transition-all ${
                  activeTab === 'seats'
                    ? 'bg-[#FF6A2A] text-white'
                    : 'bg-[#F7F8FA] text-[#6B7280] hover:bg-[#E5E7EB]'
                }`}
              >
                Seleccionar Asientos
              </button>
              <button
                onClick={() => setActiveTab('amenities')}
                className={`px-4 py-2 rounded-t-[10px] font-semibold text-sm transition-all ${
                  activeTab === 'amenities'
                    ? 'bg-[#FF6A2A] text-white'
                    : 'bg-[#F7F8FA] text-[#6B7280] hover:bg-[#E5E7EB]'
                }`}
              >
                Amenidades y Fotos
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`px-4 py-2 rounded-t-[10px] font-semibold text-sm transition-all ${
                  activeTab === 'reviews'
                    ? 'bg-[#FF6A2A] text-white'
                    : 'bg-[#F7F8FA] text-[#6B7280] hover:bg-[#E5E7EB]'
                }`}
              >
                Reseñas
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-6 bg-[#FAFAFA]">
              {activeTab === 'seats' && (
                <div>
                  {/* Boarding Points */}
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div className="bg-white p-4 rounded-[12px] border border-[#E5E7EB]">
                      <h4 className="font-semibold text-[#111827] mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#FF6A2A]" />
                        Puntos de Abordaje
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-[#6B7280]" />
                          <div>
                            <p className="text-sm font-medium text-[#111827]">{departureTime}</p>
                            <p className="text-xs text-[#6B7280]">{departureLocation}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-[12px] border border-[#E5E7EB]">
                      <h4 className="font-semibold text-[#111827] mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#10B981]" />
                        Puntos de Descenso
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-[#6B7280]" />
                          <div>
                            <p className="text-sm font-medium text-[#111827]">{arrivalTime}</p>
                            <p className="text-xs text-[#6B7280]">{arrivalLocation}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Seat Map */}
                  <SeatMap selectedSeats={selectedSeats} onSeatSelect={handleSeatSelect} />

                  {/* Continue Button */}
                  {selectedSeats.length > 0 && (
                    <div className="mt-6 flex items-center justify-between bg-white p-6 rounded-[12px] border-2 border-[#FF6A2A]">
                      <div>
                        <p className="text-sm text-[#6B7280] mb-1">Total a pagar</p>
                        <p className="text-3xl font-bold text-[#111827]">
                          ${totalPrice.toLocaleString('es-MX')}
                        </p>
                        <p className="text-xs text-[#6B7280] mt-1">
                          {selectedSeats.length} {selectedSeats.length === 1 ? 'asiento seleccionado' : 'asientos seleccionados'}
                        </p>
                      </div>
                      <Button variant="primary" className="px-8 py-3 text-base" onClick={handleContinue}>
                        Continuar con pasajeros
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'amenities' && (
                <div className="bg-white p-6 rounded-[12px] border border-[#E5E7EB]">
                  <h4 className="font-semibold text-[#111827] mb-4">Amenidades del autobús</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {amenities.map((amenity, index) => {
                      const Icon = amenityIcons[amenity] || AirVent;
                      return (
                        <div key={index} className="flex items-center gap-3 p-3 bg-[#F7F8FA] rounded-[10px]">
                          <Icon className="w-5 h-5 text-[#FF6A2A]" />
                          <span className="text-sm font-medium text-[#111827]">{amenity}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="bg-white p-6 rounded-[12px] border border-[#E5E7EB]">
                  <h4 className="font-semibold text-[#111827] mb-4">Reseñas de pasajeros</h4>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-[#111827]">{rating}</div>
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="w-4 h-4 fill-[#FF6A2A] text-[#FF6A2A]" />
                        <Star className="w-4 h-4 fill-[#FF6A2A] text-[#FF6A2A]" />
                        <Star className="w-4 h-4 fill-[#FF6A2A] text-[#FF6A2A]" />
                        <Star className="w-4 h-4 fill-[#FF6A2A] text-[#FF6A2A]" />
                        <Star className="w-4 h-4 fill-[#FF6A2A] text-[#FF6A2A]" />
                      </div>
                      <p className="text-xs text-[#6B7280] mt-1">{ratingCount} reseñas</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="p-4 bg-[#F7F8FA] rounded-[10px]">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-[#FF6A2A] rounded-full flex items-center justify-center text-white text-sm font-bold">
                          JM
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#111827]">Juan Martínez</p>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 fill-[#FF6A2A] text-[#FF6A2A]" />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-[#6B7280]">
                        Excelente servicio, autobús muy cómodo y limpio. Llegamos a tiempo.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Modal */}
      <SeatSelectionModal
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        bookingData={{
          company,
          departureTime,
          arrivalTime,
          duration,
          price,
        }}
        selectedSeats={selectedSeats}
        onSeatSelect={handleSeatSelect}
        onConfirm={() => {
          setShowMobileModal(false);
          handleContinue();
        }}
      />
    </>
  );
};